import { ArrowDropDown, DateRange, Done } from '@mui/icons-material';
import {
    Button, Divider, Grid, List, ListItem, ListItemButton, Popover, Typography,
} from '@mui/material';
import { isValid } from 'date-fns';
import React, { useRef, useState } from 'react';
import { formatDateForDisplay } from '../../../utils/dateTimeUtil';
import ActionButton from '../../ActionButton/ActionButton';
import DatePicker from '../DatePicker/DatePicker';
import { getDatesFromPeriod } from './DateRangePickerUtils';
import { isTextValidAndNotEmpty } from '../../../utils/nullChecks';
import { DateAndPeriod, DatePeriod } from '../types';

type Props = {
    selectedValue: DateAndPeriod,
    onDateAndPeriodChange: (a: DateAndPeriod) => void,
    datePeriodOptions: Array<DatePeriod>,
    startOfDay?: boolean,
    endOfDay?: boolean,
    maxDate?: Date,
    minDate?: Date,
    fullWidth?: boolean,
};

const DateRangePicker = (props: Props) => {
    const popoverRef = useRef(null);
    const [datePopover, setDatePopover] = useState<boolean>(false);
    const {
        selectedValue,
        onDateAndPeriodChange,
        datePeriodOptions,
        startOfDay,
        endOfDay,
        maxDate,
        minDate,
        fullWidth,
    } = props;

    const [startDate, setStartDate] = useState<Date | null>(selectedValue.startDate);
    const [endDate, setEndDate] = useState<Date | null>(selectedValue.endDate);

    const updateStartDate = (newDate:Date | null) => {
        setStartDate(newDate);
    };
    const updateEndDate = (newDate:Date | null) => {
        setEndDate(newDate);
    };
    const togglePopper = () => {
        if (!datePopover) {
            updateEndDate(selectedValue.endDate);
            updateStartDate(selectedValue.startDate);
        }
        setDatePopover(!datePopover);
    };

    const handleApply = () => {
        onDateAndPeriodChange({
            startDate,
            endDate,
        });
        togglePopper();
    };

    const handleClear = () => {
        onDateAndPeriodChange({
            startDate: null, endDate: null,
        });
        setStartDate(null);
        setEndDate(null);
        togglePopper();
    };
    const applyOnSelect = (selectedItem: DatePeriod) => () => {
        const datesAndPeriod = getDatesFromPeriod(selectedItem, startOfDay, endOfDay);
        onDateAndPeriodChange(datesAndPeriod);
        togglePopper();
    };
    const getLabel = (dateAndPeriod:DateAndPeriod) => {
        if (isTextValidAndNotEmpty(dateAndPeriod?.period)) {
            return dateAndPeriod?.period;
        }
        let dateString = 'Date';
        if (isValid(dateAndPeriod.startDate)) {
            dateString = formatDateForDisplay(dateAndPeriod.startDate);
        }
        if (isValid(dateAndPeriod.endDate)) {
            dateString = `${dateString}${dateString ? ' - ' : ''}${formatDateForDisplay(dateAndPeriod.endDate)}`;
        }
        return (
            <>
                <DateRange />
                { dateString }
            </>
        );
    };
    return (
        <>
            <Button
                onClick={togglePopper}
                ref={popoverRef}
                endIcon={<ArrowDropDown />}
                fullWidth={fullWidth}
                sx={{
                    justifyContent: 'space-between',
                }}
            >
                {getLabel(selectedValue)}
            </Button>
            <Popover
                open={datePopover}
                onClose={togglePopper}
                anchorEl={popoverRef.current}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'left',
                }}
            >
                <List>
                    {
                        datePeriodOptions.map((period) => (
                            <ListItem
                                key={period}
                                disablePadding
                                secondaryAction={
                                    period === selectedValue.period ? (
                                        <Done
                                            sx={(theme) => ({
                                                color: theme.palette.primary.main,
                                            })}
                                        />
                                    ) : null
                                }
                            >
                                <ListItemButton
                                    selected={period === selectedValue.period}
                                    onClick={applyOnSelect(period)}
                                >
                                    <Typography
                                        sx={(theme) => (period === selectedValue.period ? {
                                            color: theme.palette.primary.main,
                                        } : {})}
                                    >
                                        {period}
                                    </Typography>
                                </ListItemButton>
                            </ListItem>
                        ))
                    }
                </List>
                <Divider />
                <div>
                    <Grid
                        container
                        spacing={1}
                        rowSpacing={1}
                        padding={2}
                        width="20vw"
                    >
                        <Grid item sm={12}>
                            <Typography
                                color="GrayText"
                            >
                                Custom Range
                            </Typography>
                        </Grid>
                        <Grid item sm={6}>
                            <DatePicker
                                value={startDate || selectedValue.startDate}
                                onChange={updateStartDate}
                                label="From"
                                minDate={minDate}
                                maxDate={endDate || undefined}
                            />
                        </Grid>
                        <Grid item sm={6}>
                            <DatePicker
                                value={endDate || selectedValue.endDate}
                                onChange={updateEndDate}
                                label="To"
                                minDate={startDate || undefined}
                                maxDate={maxDate}
                            />
                        </Grid>
                        <Grid
                            item
                            sm={12}
                            container
                            justifyContent="flex-end"
                        >
                            <ActionButton
                                onClick={handleClear}
                                variant="outlined"
                            >
                                Clear
                            </ActionButton>
                            <ActionButton
                                onClick={handleApply}
                                variant="contained"
                            >
                                Apply
                            </ActionButton>
                        </Grid>
                    </Grid>
                </div>
            </Popover>
        </>
    );
};

DateRangePicker.defaultProps = {
    startOfDay: false,
    endOfDay: false,
    maxDate: null,
    minDate: null,
    fullWidth: false,
};

export default DateRangePicker;
